import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { Card, formatDate, Page, State } from '../components';
import { safeCitationUrl, type Citation } from '@24talk/shared';
import { MessageMarkdown } from '../MessageMarkdown';

interface Conversation {
  id: string;
  status: string;
  lastMessageAt: string;
  agent: { displayName: string };
  _count: { messages: number; handoffs: number };
}
interface Detail extends Conversation {
  messages: Array<{
    id: string;
    role: string;
    content: string;
    citations?: Citation[] | null;
    createdAt: string;
  }>;
  handoffs: Array<{ id: string; reason: string; status: string }>;
}

export function ConversationsPage() {
  const query = useQuery({
    queryKey: ['conversations'],
    queryFn: () => api<{ data: Conversation[] }>('/api/v1/conversations'),
  });
  return (
    <Page
      title="Conversations"
      description="Visitor and assistant history is stored in the platform database."
    >
      <State loading={query.isLoading} error={query.error} empty={query.data?.data.length === 0}>
        <div className="card table-scroll">
          <table>
            <thead>
              <tr>
                <th>Agent</th>
                <th>Status</th>
                <th>Messages</th>
                <th>Last activity</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {query.data?.data.map((row) => (
                <tr key={row.id}>
                  <td>{row.agent.displayName}</td>
                  <td>
                    <span className="badge">{row.status}</span>
                  </td>
                  <td>{row._count.messages}</td>
                  <td>{formatDate(row.lastMessageAt)}</td>
                  <td>
                    <Link to={`/conversations/${row.id}`}>Review</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </State>
    </Page>
  );
}

export function ConversationDetailPage() {
  const { id } = useParams();
  const query = useQuery({
    queryKey: ['conversation', id],
    queryFn: () => api<Detail>(`/api/v1/conversations/${id}`),
  });
  return (
    <Page
      title="Conversation detail"
      description="A chronological, tenant-scoped transcript."
      actions={
        <Link className="button" to="/conversations">
          Back
        </Link>
      }
    >
      <State loading={query.isLoading} error={query.error}>
        {query.data && (
          <div className="detail-grid">
            <Card>
              <div className="conversation-meta">
                <span>{query.data.agent.displayName}</span>
                <span className="badge">{query.data.status}</span>
              </div>
              <div className="transcript">
                {query.data.messages.map((message) => (
                  <article key={message.id} className={`message ${message.role.toLowerCase()}`}>
                    <small>
                      {message.role} · {formatDate(message.createdAt)}
                    </small>
                    {message.role === 'ASSISTANT' ? (
                      <MessageMarkdown text={message.content} citations={message.citations} />
                    ) : (
                      <p>{message.content}</p>
                    )}
                    {message.citations?.length ? (
                      <ul>
                        {message.citations.map((citation, index) => (
                          <li key={index}>
                            {safeCitationUrl(citation.url) ? (
                              <a
                                href={safeCitationUrl(citation.url)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {citation.title || citation.url}
                              </a>
                            ) : (
                              (citation.filename ?? 'Knowledge source')
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </article>
                ))}
              </div>
            </Card>
            <Card>
              <h2>Human handoffs</h2>
              {query.data.handoffs.length ? (
                query.data.handoffs.map((item) => (
                  <div className="side-record" key={item.id}>
                    <span className="badge">{item.status}</span>
                    <p>{item.reason}</p>
                  </div>
                ))
              ) : (
                <p>No handoff requested.</p>
              )}
            </Card>
          </div>
        )}
      </State>
    </Page>
  );
}
